import type { TenantsWithPropertiesQuery, Properties_Bool_Exp } from '@/gql/graphql'

/**
 * Pure tenant/property mapping — shared by services/tenants (client fetch) and
 * the dashboard layout's server seed (lib/dashboard/header-server.ts). Kept in
 * its own module (no gqlRequest / browser-SDK imports) so Server Components
 * can import it.
 */

export interface Property {
  id: string
  code: string
  name_en: string
  /** V-07: the Arabic name, shown to an Arabic reader where the hotel has one. */
  name_ar?: string | null
}

export interface Tenant {
  id: string
  code: string
  name_en: string
  /** V-07: the Arabic name (display only). */
  name_ar?: string | null
  is_active: boolean
  pdf_logo_url: string | null
  properties: Property[]
}

/**
 * Roles whose `properties` SELECT RLS is TENANT-scoped (see
 * taskyinn-backend/.../tables/public_properties.yaml) — widened so the inbox can
 * list every branch for conversation routing. For these roles the tenant/property
 * SWITCHER must be re-pinned to the user's memberships explicitly; switching the
 * active property is a membership decision, not a visibility one.
 */
const TENANT_SCOPED_PROPERTIES_ROLES = new Set(['front_desk', 'manager'])

/**
 * Explicit membership pin for the switcher's property list (TenantsWithProperties).
 * `{}` (no-op) for every other role: their RLS is already membership-pinned, and the
 * platform `admin` role bypasses RLS entirely — pinning would EMPTY an admin's list
 * (admins have no property_members rows), so the pin only applies where RLS widened.
 */
export function switcherPropertiesWhere(
  userId: string | null | undefined,
  role: string | null | undefined
): Properties_Bool_Exp {
  if (!userId || !role || !TENANT_SCOPED_PROPERTIES_ROLES.has(role)) return {}
  return { property_member_access: { tenant_member: { user_id: { _eq: userId } } } }
}

export function mapTenantsWithProperties(
  data: TenantsWithPropertiesQuery | null | undefined
): Tenant[] {
  return (
    data?.tenants?.map(t => ({
      id: t.id,
      code: t.code,
      name_en: t.name_en,
      name_ar: t.name_ar ?? null,
      // Default missing/null to active so a tenant is never hidden by accident.
      is_active: t.is_active ?? true,
      pdf_logo_url: t.pdf_logo_url ?? null,
      properties: (t.properties || []).map(p => ({ id: p.id, code: p.code, name_en: p.name_en, name_ar: p.name_ar ?? null })),
    })) || []
  )
}
